import React, { useEffect, useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import DetailWrapper from '../../../sharedcomponents/4layouts/DetailWrapper'
import useDetailEditMode from '../../../sharedcomponents/4layouts/useDetailEditMode'
import LeftCard from '../../../sharedcomponents/5templates/detailCards/leftCard'
import ListBucketCard from '../../../sharedcomponents/5templates/detailCards/listBucketCard'
import useLocation from './useLocation'
import LeftChildBuilding from './/LeftChildBuilding'
import LeftChildMobile from './LeftChildMobile'
import LeftChildMulti from './LeftChildMulti'
import LeftChildSatellite from './LeftChildSatellite'
import LeftChildSingle from './LeftChildSingle'
import LeftChildSite from './leftChildSite'
import { ArtificialDetailPropertyNames } from '../../../common/globalNotary'

const AssetDetail = ({ createMode }) => {
  const [types, setTypes] = useState([])
  let { assetId } = useParams()
  const [formType, setFormType] = useState('single')
  const formikFunctionBucket = {}

  const { detailEditMode, setDetailEditMode } = useDetailEditMode(createMode)

  const {
    entityDisplayNameFromParams,
    listAssetTypes,
    bulkUpdateLocationsCallBack,
    useGetOneAssetQuery,
    fetchAuxiliaryRecords,
    makeInMemoryLocationRecord,
    finalAuxiliaryRecords,
    upsertLocationCallback,
    fetchBuildingAddress,
    removeLocation,
    suspendAsset,
    loading: updateLocationLoading
  } = useLocation()

  const {
    data: { data: rawAssetRecord } = {
      data: {}
    },
    refetch,
    isLoading,
    isSuccess
  } = useGetOneAssetQuery(
    {
      include: ['counts'],
      id: assetId
    },
    { skip: createMode, refetchOnMountOrArgChange: true }
  )

  const loading = useMemo(() => updateLocationLoading || isLoading, [updateLocationLoading, isLoading])

  const primaryAssetRecord = createMode
    ? makeInMemoryLocationRecord(formType)
    : JSON.parse(JSON.stringify(rawAssetRecord))
  primaryAssetRecord.refetch = refetch


  useEffect(() => {
    const getData = async () => {
      if (!createMode && rawAssetRecord.type_id) {
        const allTypes = await listAssetTypes()
        setTypes(
          allTypes.filter(type => type.config.is_container === false)
        )

        // Update the form with the correct type now we've
        // loaded in all the types
        const foundType = allTypes.find(t => t.id === rawAssetRecord.type_id)
        formikFunctionBucket.setFieldValue(
          'type_id',
          foundType.id,
          false
        )
        formikFunctionBucket.setFieldValue(
          'config',
          foundType.config,
          false
        )

        setFormType(foundType.base_type)
      }
    }
    getData()

    // This one legit cannot contain the listAssetTypes
    // eslint-disable-next-line
  }, [rawAssetRecord.type_id])

  useEffect(() => {
    const getData = async () => {
      if (createMode) {
        const allTypes = await listAssetTypes()
        setTypes(
          allTypes.filter(type => type.config.is_container === false)
        )

        // Update the form with the correct type now we've
        // loaded in all the types
        const initialType = allTypes.find(t => t.base_type === formType)
        formikFunctionBucket.setFieldValue(
          'type_id',
          initialType.id,
          false
        )
        formikFunctionBucket.setFieldValue(
          'config',
          initialType.config,
          false
        )
      }
    }
    getData()

    // This one legit cannot contain the listAssetTypes
    // eslint-disable-next-line
  }, [])

  const typeIdToDisplayName = typeId => {
    typeId = typeId ?? primaryAssetRecord.type_id
    return types?.find(t => t.id === typeId)?.name ?? ''
  }

  const onTypeChange = (formikProps) => (event) => {
    const newType = types.find(t => t.id === event.target.value)

    if (newType) {
      setFormType(newType.base_type)
      // Set the new type for this asset
      formikProps.setFieldValue('type_id', newType.id)
      // Set the new embedded type config on this object
      formikProps.setFieldValue('config', newType.config)
    }
  }

  const leftChildFromParams = (type, formikProps) => {
    if (primaryAssetRecord.type_id && !createMode) {
      const typeArray =
        primaryAssetRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]

      const typeDef = typeArray.find(o => {
        return o.id === primaryAssetRecord.type_id
      })

      primaryAssetRecord.config = typeDef.config
    }

    switch (type) {
      case 'multi':
        return (
          <LeftChildMulti
            formikProps={formikProps}
            editMode={detailEditMode}
            createMode={createMode}
            assetTypes={types}
            entities={finalAuxiliaryRecords?.entities}
            buildings={finalAuxiliaryRecords?.buildings}
            onTypeChange={onTypeChange(formikProps)}
          />
        )
      case 'single':
        return (
          <LeftChildSingle
            formikProps={formikProps}
            editMode={detailEditMode}
            createMode={createMode}
            assetTypes={types}
            entities={finalAuxiliaryRecords?.entities}
            onTypeChange={onTypeChange(formikProps)}
          />
        )

      case 'building':
        return (
          <LeftChildBuilding
            formikProps={formikProps}
            editMode={detailEditMode}
            createMode={createMode}
            assetTypes={types}
            entities={finalAuxiliaryRecords?.entities}
            onTypeChange={onTypeChange(formikProps)}
          />
        )

      case 'satellite':
        return (
          <LeftChildSatellite
            formikProps={formikProps}
            editMode={detailEditMode}
            createMode={createMode}
            assetTypes={types}
            entities={finalAuxiliaryRecords?.entities}
            onTypeChange={onTypeChange(formikProps)}
          />
        )

      case 'mobile':
        return (
          <LeftChildMobile
            formikProps={formikProps}
            editMode={detailEditMode}
            createMode={createMode}
            assetTypes={types}
            entities={finalAuxiliaryRecords?.entities}
            onTypeChange={onTypeChange(formikProps)}
          />
        )

      case 'site':
        return (
          <LeftChildSite
            formikProps={formikProps}
            editMode={detailEditMode}
            createMode={createMode}
            assetTypes={types}
            entities={finalAuxiliaryRecords?.entities}
            onTypeChange={onTypeChange(formikProps)}
          />
        )

      default:
        throw new Error('unknown location type')
    }
  }

  const observeValueChange = async values => {
    if (values.hasOwnProperty('parent_id')) {
      const newParentId = values.parent_id
      const oldParentId = primaryAssetRecord.parent_id

      if (oldParentId !== newParentId) {
        const buildingAddress = await fetchBuildingAddress(newParentId)

        if (formikFunctionBucket.setFieldValue) {
          formikFunctionBucket.setFieldValue(
            'address.country',
            buildingAddress.country,
            false
          )
          formikFunctionBucket.setFieldValue(
            'address.region',
            buildingAddress.region,
            false
          )
          formikFunctionBucket.setFieldValue(
            'address.city',
            buildingAddress.city,
            false
          )
          formikFunctionBucket.setFieldValue(
            'address.postal',
            buildingAddress.postal,
            false
          )
          formikFunctionBucket.setFieldValue(
            'address.locale',
            buildingAddress.locale,
            false
          )

          formikFunctionBucket.setFieldValue(
            'address.unit',
            buildingAddress.unit,
            false
          )
          formikFunctionBucket.setFieldValue(
            'address.street',
            buildingAddress.street,
            false
          )

          formikFunctionBucket.setFieldValue(
            'address.lat',
            buildingAddress.lat,
            false
          )

          formikFunctionBucket.setFieldValue(
            'address.long',
            buildingAddress.long,
            false
          )
        }
      }
    }
  }

  return <DetailWrapper
      entityDisplayName={entityDisplayNameFromParams(formType)}
      detailEditMode={detailEditMode}
      setDetailEditMode={setDetailEditMode}
      typeIdToDisplayName={typeIdToDisplayName}
      primaryDetailRecord={primaryAssetRecord}
      isLoading={!createMode && (loading || !isSuccess)}
      secondaryDataLoaderCallBack={fetchAuxiliaryRecords}
      upsertDetailCallback={upsertLocationCallback}
      bulkUpdateCallBack={bulkUpdateLocationsCallBack}
      finalAuxiliaryRecords={finalAuxiliaryRecords}
      optionalListenerToFormikValidation={observeValueChange}
    >
      {({ formikProps, activationChangeCallback, deleteEntityCallback }) => {
        formikFunctionBucket.setFieldValue = formikProps.setFieldValue
        return (
          <>
            <div style={{ float: 'left', width: '34%' }}>
              <LeftCard
                entityDisplayName={entityDisplayNameFromParams(formType)}
                formikProps={formikProps}
                editMode={detailEditMode}
                typeIdToDisplayName={typeIdToDisplayName}
                deleteEntityCallback={deleteEntityCallback(removeLocation)}
                activationChangeCallback={activationChangeCallback(suspendAsset)}
                listOfTypes={types}
                arrayOfBucketConfig={finalAuxiliaryRecords?.primero}
              >
                {leftChildFromParams(formType, formikProps)}
              </LeftCard>
            </div>
            <div style={{ float: 'left', width: '33%' }}>
              <ListBucketCard
                formikProps={formikProps}
                editMode={detailEditMode}
                arrayOfBucketConfig={finalAuxiliaryRecords?.segundo}
              />
            </div>
            <div style={{ float: 'right', width: '33%' }}>
              <ListBucketCard
                formikProps={formikProps}
                editMode={detailEditMode}
                arrayOfBucketConfig={finalAuxiliaryRecords?.tercero}
              />
            </div>
          </>
        )
      }}
    </DetailWrapper>
}
export default AssetDetail
